import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import { Close, Send } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import shipmentService from "../../services/shipmentService";
import emailContactService from "../../services/emailContactService";
import RecipientPicker from "../common/RecipientPicker";

// Workflow 2 — "Authorization Email": admin has already checked items on
// the Ready to Sail page and is asking the freight forwarder to authorize
// those dock receipts for the next sailing. Purely "preview the email, pick
// a recipient, send it" — the D/Rs were already consolidated (and
// invoiced) via a prior Consolidate Items email, so no invoice is attached
// here and nothing in the app is mutated.
const AssignDockReceiptsDialog = ({ open, onClose, items, onSent }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [loadingPreview, setLoadingPreview] = useState(false);
    const [preview, setPreview] = useState(null); // { subject, html }
    const [previewError, setPreviewError] = useState("");
    const [contacts, setContacts] = useState([]);
    const [to, setTo] = useState("");
    const [sending, setSending] = useState(false);

    const loadContacts = () => {
        emailContactService
            .getAll()
            .then(({ data }) => setContacts(data.data.contacts || []))
            .catch(() => setContacts([]));
    };

    useEffect(() => {
        if (!open) return;
        setTo("");
        setPreview(null);
        setPreviewError("");
        loadContacts();
        setLoadingPreview(true);
        shipmentService
            .previewAssignDrEmail(items)
            .then(({ data }) => setPreview(data.data))
            .catch((err) =>
                setPreviewError(err.response?.data?.message || "Failed to build email preview"),
            )
            .finally(() => setLoadingPreview(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSend = async () => {
        if (!to.trim()) return;
        setSending(true);
        try {
            const { data } = await shipmentService.sendAssignDrEmail(items, to.trim());
            enqueueSnackbar(data.message, { variant: "success" });
            onSent?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to send email", { variant: "error" });
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontWeight={700}>Authorization Email — {items.length} item(s)</Typography>
                <IconButton size="small" onClick={onClose} disabled={sending}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {previewError && <Alert severity="error">{previewError}</Alert>}

                    {loadingPreview ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        preview && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Email preview
                                </Typography>
                                {/* Always white/dark-text, independent of app theme —
                                    this previews an actual email, which always
                                    renders on a white backdrop in every real inbox,
                                    and the HTML itself assumes that. */}
                                <Box
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 2,
                                        mt: 0.5,
                                        maxHeight: 320,
                                        overflowY: "auto",
                                        bgcolor: "#fff",
                                        color: "#0E1420",
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={700} mb={1} sx={{ color: "#0E1420" }}>
                                        Subject: {preview.subject}
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Box dangerouslySetInnerHTML={{ __html: preview.html }} />
                                </Box>
                            </Box>
                        )
                    )}

                    <RecipientPicker value={to} onChange={setTo} contacts={contacts} onSaved={loadContacts} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined" disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
                    onClick={handleSend}
                    disabled={sending || !to.trim() || !preview}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignDockReceiptsDialog;
